/**
 * Firebase Performance Monitoring and Analytics
 * Tracks app performance, user behavior, and errors
 *
 * NOTE: This module does NOT initialize its own Firebase app.
 * It reuses the app initialized by FirebaseAuth.tsx to avoid conflicts.
 * Analytics and Performance are only enabled when proper config exists.
 */

import { getApps } from "firebase/app";
import {
  getAnalytics,
  logEvent,
  setUserId,
  setUserProperties,
} from "firebase/analytics";
import { getPerformance, trace } from "firebase/performance";

// Initialize services - defer until Firebase app is available
let analytics: ReturnType<typeof getAnalytics> | null = null;
let performance: ReturnType<typeof getPerformance> | null = null;
let monitoringInitialized = false;

/**
 * Check if analytics config is valid (not placeholder values)
 */
function hasValidAnalyticsConfig(): boolean {
  const appId = import.meta.env.VITE_FIREBASE_APP_ID;
  const measurementId = import.meta.env.VITE_FIREBASE_MEASUREMENT_ID;

  // Check for missing or placeholder values
  if (!appId || !measurementId) return false;
  if (appId.includes("placeholder")) return false;
  if (measurementId === "G-PLACEHOLDER") return false;

  return true;
}

/**
 * Initialize monitoring services (called after Firebase app is ready)
 */
function initializeMonitoringServices(): void {
  if (monitoringInitialized) return;
  if (typeof window === "undefined") return;

  const apps = getApps();
  if (apps.length === 0) {
    // Firebase app not yet initialized - will retry on first event
    return;
  }

  const app = apps[0];

  // Only initialize analytics if we have valid config
  if (hasValidAnalyticsConfig()) {
    try {
      analytics = getAnalytics(app);
      performance = getPerformance(app);
      console.log(
        "[Monitoring] Firebase Analytics and Performance initialized"
      );
    } catch (error) {
      // Analytics/Performance may not be available in all environments
      console.log(
        "[Monitoring] Analytics/Performance not available:",
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  } else {
    console.log(
      "[Monitoring] Analytics disabled - no valid config (this is normal in dev)"
    );
  }

  monitoringInitialized = true;
}

/**
 * Track custom events
 */
export function trackEvent(eventName: string, params?: Record<string, any>) {
  // Try to initialize services if not done yet
  initializeMonitoringServices();

  if (!analytics) return;

  try {
    logEvent(analytics, eventName, {
      ...params,
      timestamp: new Date().toISOString(),
      region: "europe-west6", // Swiss region
    });
  } catch (error) {
    console.error("[Monitoring] Failed to track event:", error);
  }
}

/**
 * Track page views
 */
export function trackPageView(pageName: string, params?: Record<string, any>) {
  trackEvent("page_view", {
    page_title: pageName,
    ...params,
  });
}

/**
 * Track user identification
 */
export function identifyUser(userId: string, properties?: Record<string, any>) {
  // Try to initialize services if not done yet
  initializeMonitoringServices();

  if (!analytics) return;

  try {
    setUserId(analytics, userId);
    if (properties) {
      setUserProperties(analytics, properties);
    }
    console.log("[Monitoring] User identified:", userId);
  } catch (error) {
    console.error("[Monitoring] Failed to identify user:", error);
  }
}

/**
 * Track errors
 */
export function trackError(error: Error, context?: Record<string, any>) {
  console.error("[Error]", error, context);

  trackEvent("error", {
    error_name: error.name,
    error_message: error.message,
    error_stack: error.stack?.substring(0, 500), // Limit stack trace size
    ...context,
  });
}

/**
 * Track API calls
 */
export function trackApiCall(
  endpoint: string,
  method: string,
  duration: number,
  status: "success" | "error",
  statusCode?: number
) {
  trackEvent("api_call", {
    endpoint,
    method,
    duration_ms: duration,
    status,
    status_code: statusCode,
  });
}

/**
 * Performance tracing
 */
export class PerformanceTrace {
  private traceName: string;
  private startTime: number;
  private trace: ReturnType<typeof trace> | null = null;

  constructor(traceName: string) {
    this.traceName = traceName;
    this.startTime = Date.now();

    // Try to initialize services if not done yet
    initializeMonitoringServices();

    if (performance) {
      try {
        this.trace = trace(performance, traceName);
        this.trace.start();
      } catch (error) {
        console.warn("[Monitoring] Failed to start trace:", error);
      }
    }
  }

  /**
   * Add custom attribute to trace
   */
  putAttribute(name: string, value: string) {
    if (this.trace) {
      try {
        this.trace.putAttribute(name, value);
      } catch (error) {
        console.warn("[Monitoring] Failed to add attribute:", error);
      }
    }
  }

  /**
   * Add custom metric to trace
   */
  putMetric(name: string, value: number) {
    if (this.trace) {
      try {
        this.trace.putMetric(name, value);
      } catch (error) {
        console.warn("[Monitoring] Failed to add metric:", error);
      }
    }
  }

  /**
   * Stop the trace
   */
  stop() {
    const duration = Date.now() - this.startTime;

    if (this.trace) {
      try {
        this.trace.stop();
      } catch (error) {
        console.warn("[Monitoring] Failed to stop trace:", error);
      }
    }

    // Also track as custom event
    trackEvent("performance_trace", {
      trace_name: this.traceName,
      duration_ms: duration,
    });

    return duration;
  }
}

/**
 * Track component render performance
 */
export function trackComponentRender(componentName: string) {
  return new PerformanceTrace(`component_${componentName}`);
}

/**
 * Track tRPC query performance
 */
export function trackQuery(queryName: string) {
  return new PerformanceTrace(`query_${queryName}`);
}

/**
 * Track tRPC mutation performance
 */
export function trackMutation(mutationName: string) {
  return new PerformanceTrace(`mutation_${mutationName}`);
}

/**
 * Business metrics tracking
 */
export const businessMetrics = {
  /**
   * Track when a company is created
   */
  companyCreated: (companyId: number, companyName: string) => {
    trackEvent("company_created", {
      company_id: companyId,
      company_name: companyName,
    });
  },

  /**
   * Track when transactions are imported
   */
  transactionsImported: (count: number, source: string) => {
    trackEvent("transactions_imported", {
      transaction_count: count,
      import_source: source,
    });
  },

  /**
   * Track when a tax report is generated
   */
  taxReportGenerated: (reportType: string, period: string) => {
    trackEvent("tax_report_generated", {
      report_type: reportType,
      reporting_period: period,
    });
  },

  /**
   * Track document uploads
   */
  documentUploaded: (documentType: string, sizeBytes: number) => {
    trackEvent("document_uploaded", {
      document_type: documentType,
      file_size_bytes: sizeBytes,
      file_size_mb: (sizeBytes / 1024 / 1024).toFixed(2),
    });
  },

  /**
   * Track bank reconciliation
   */
  bankReconciliation: (matchedCount: number, unmatchedCount: number) => {
    trackEvent("bank_reconciliation", {
      matched_transactions: matchedCount,
      unmatched_transactions: unmatchedCount,
      match_rate: (
        (matchedCount / (matchedCount + unmatchedCount)) *
        100
      ).toFixed(1),
    });
  },
};

/**
 * Initialize monitoring on app start
 */
export function initializeMonitoring() {
  // Try to initialize Firebase services
  initializeMonitoringServices();

  // Track app start (will be a no-op if analytics not available)
  trackEvent("app_start", {
    environment: import.meta.env.MODE,
    timestamp: new Date().toISOString(),
  });

  // Track unhandled errors
  window.addEventListener("error", event => {
    trackError(event.error, {
      type: "unhandled_error",
      message: event.message,
      filename: event.filename,
      line: event.lineno,
      column: event.colno,
    });
  });

  // Track unhandled promise rejections
  window.addEventListener("unhandledrejection", event => {
    trackError(
      event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason)),
      {
        type: "unhandled_rejection",
      }
    );
  });

  console.log(
    "[Monitoring] Monitoring initialized (analytics:",
    analytics ? "enabled" : "disabled",
    ")"
  );
}
